import { version as REACT_VERSION } from 'react';

import memoizeOne from 'memoize-one';

import { EXTERNALS } from '../../../common/constants';
import {
	type DependencyMap,
	type ManifestMap,
	type ModuleUriMapper,
	type PluginDimensions,
	PluginName,
	type RemotePlugin,
} from '../../../types';

export function getModuleUri(env: string): string {
	if (window) {
		const { origin } = window.location;
		const isIsolatedCloud =
			origin.endsWith('.oasis-stg.com') || origin.endsWith('.atlassian-isolated.net');
		if (isIsolatedCloud) {
			return origin + '/remote-app-switcher/';
		}
	}
	return env === 'staging'
		? 'https://remote-app-switcher.stg-east.frontend.public.atl-paas.net/'
		: 'https://remote-app-switcher.prod-east.frontend.public.atl-paas.net/';
}

const MODULE_NAME = '@atlassian/app-switcher-remote-module';
const MODULE_URI_STG = getModuleUri('staging');
const MODULE_URI_PROD = getModuleUri('prod');
const MODULE_URI_STG_FEDM_EAST =
	'https://remote-app-switcher.stg.frontend.cdn.atlassian-us-gov-mod.com/';
const MODULE_URI_PROD_FEDM_EAST =
	'https://remote-app-switcher.frontend.cdn.atlassian-us-gov-mod.com/';

class RemoteSwitcherPlugin implements RemotePlugin {
	private readonly _dimensions: PluginDimensions;
	private readonly _manifestMap: ManifestMap = new Map([
		[
			'local',
			new Map([
				[
					'latest',
					{
						moduleName: MODULE_NAME,
						moduleUri: 'http://localhost:9001/',
						externals: EXTERNALS,
					},
				],
			]),
		],
		[
			'staging',
			new Map([
				[
					'latest',
					{
						moduleName: MODULE_NAME,
						moduleUri: MODULE_URI_STG,
						externals: EXTERNALS,
					},
				],
				[
					'stable',
					{
						moduleName: MODULE_NAME,
						moduleUri: MODULE_URI_STG,
						externals: EXTERNALS,
					},
				],
			]),
		],
		[
			'production',
			new Map([
				[
					'latest',
					{
						moduleName: MODULE_NAME,
						moduleUri: MODULE_URI_PROD,
						externals: EXTERNALS,
					},
				],
				[
					'stable',
					{
						moduleName: MODULE_NAME,
						moduleUri: MODULE_URI_PROD,
						externals: EXTERNALS,
					},
				],
			]),
		],
		[
			'stg-fedm-east',
			new Map([
				[
					'latest',
					{
						moduleName: MODULE_NAME,
						moduleUri: MODULE_URI_STG_FEDM_EAST,
						externals: EXTERNALS,
					},
				],
				[
					'stable',
					{
						moduleName: MODULE_NAME,
						moduleUri: MODULE_URI_STG_FEDM_EAST,
						externals: EXTERNALS,
					},
				],
			]),
		],
		[
			'prod-fedm-east',
			new Map([
				[
					'latest',
					{
						moduleName: MODULE_NAME,
						moduleUri: MODULE_URI_PROD_FEDM_EAST,
						externals: EXTERNALS,
					},
				],
				[
					'stable',
					{
						moduleName: MODULE_NAME,
						moduleUri: MODULE_URI_PROD_FEDM_EAST,
						externals: EXTERNALS,
					},
				],
			]),
		],
	]);

	constructor(
		dimensions: PluginDimensions,
		config?: {
			manifestMap?: ManifestMap;
			moduleUriMapper?: ModuleUriMapper;
		},
	) {
		this._dimensions = dimensions;

		// allow either of these config options to be overridden
		if (config) {
			if (config.manifestMap) {
				this._manifestMap = config.manifestMap;
			}

			if ('moduleUriMapper' in config) {
				this.moduleUriMapper = config.moduleUriMapper;
			}
		}
	}

	get bifrostOverrideKey() {
		return 'remote-app-switcher-bifrost-override';
	}

	get dimensions() {
		return this._dimensions;
	}

	get manifestMap() {
		return this._manifestMap;
	}

	get name() {
		return PluginName.AppSwitcher;
	}

	/**
	 * This is optional and when defined can be used to override the logic for creating the moduleUri.
	 * If it isn't defined or passed in then we will use the moduleUri from the manifestMap as per the defined dimensions.
	 *
	 * @param uri
	 * @param variant
	 * @param computeHash
	 */
	public moduleUriMapper: ModuleUriMapper | undefined = memoizeOne(
		(uri, variant = 'latest', computeHash) => {
			// We want to hash the major version of React installed, alongside the chosen variant
			const hashedDependencies = ['react', 'react-dom'];
			const hashedValues = hashedDependencies.reduce<DependencyMap[]>(
				(acc, dep) => acc.concat([{ name: dep, version: REACT_VERSION.split('.')[0] }]),
				[],
			);

			const hash = computeHash(variant, hashedValues);

			if (process.env.NODE_ENV === 'development') {
				// eslint-disable-next-line no-console
				console.debug(
					`[@atlassian/remote-module-loader]: Computed hash for plugin [${
						this.constructor.name
					}] with variant [${variant}] and values ${JSON.stringify(hashedValues)}.`,
				);
			}

			return `${uri}${hash}.js`;
		},
	);
}

export default RemoteSwitcherPlugin;
